import { Injectable, signal } from '@angular/core';

export type SupportedLanguage = 'en' | 'fr' | 'de';
export type SupportedDateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY/MM/DD' | 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
type TranslationDictionary = Record<string, unknown>;
const DATE_FORMAT_STORAGE_KEY = 'phd-compass-date-format';
const DEFAULT_DATE_FORMAT: SupportedDateFormat = 'DD/MM/YYYY';
const TRANSLATION_VERSION = '2026-08-27-german';

@Injectable({ providedIn: 'root' })
export class I18nService {
  readonly supportedLanguages: { code: SupportedLanguage; labelKey: string; nativeName: string }[] = [
    { code: 'en', labelKey: 'settings.language.english', nativeName: 'English' },
    { code: 'fr', labelKey: 'settings.language.french', nativeName: 'Français' },
    { code: 'de', labelKey: 'settings.language.german', nativeName: 'Deutsch' }
  ];
  readonly supportedDateFormats: SupportedDateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD'];
  readonly currentLanguage = signal<SupportedLanguage>(this.loadInitialLanguage());
  readonly currentDateFormat = signal<SupportedDateFormat>(this.loadInitialDateFormat());
  private readonly translations = signal<TranslationDictionary>({});

  constructor() {
    void this.loadLanguage(this.currentLanguage());
  }

  async setLanguage(language: SupportedLanguage): Promise<void> {
    if (!this.isSupportedLanguage(language)) return;

    this.currentLanguage.set(language);
    localStorage.setItem('phd-compass-language', language);
    await this.loadLanguage(language);
  }

  t(key: string, params: Record<string, string | number> = {}): string {
    const value = this.value<string>(key);

    const template = typeof value === 'string' ? value : key;
    return Object.entries(params).reduce(
      (text, [paramKey, paramValue]) => text.replaceAll(`{{${paramKey}}}`, String(paramValue)),
      template
    );
  }

  value<T = unknown>(key: string): T | undefined {
    return key.split('.').reduce<unknown>((current, segment) => {
      if (!current || typeof current !== 'object') return undefined;
      return (current as TranslationDictionary)[segment];
    }, this.translations()) as T | undefined;
  }

  dateLocale(): string {
    if (this.currentLanguage() === 'fr') return 'fr-FR';
    if (this.currentLanguage() === 'de') return 'de-DE';
    return 'en-US';
  }

  setDateFormat(format: SupportedDateFormat): void {
    if (!this.isSupportedDateFormat(format)) return;

    this.currentDateFormat.set(format);
    localStorage.setItem(DATE_FORMAT_STORAGE_KEY, format);
  }

  formatDate(value: string): string {
    if (!value || value === 'unknown') return this.t('common.unknown');

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return value;

    const [, year, month, day] = match;
    switch (this.currentDateFormat()) {
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY/MM/DD': return `${year}/${month}/${day}`;
      case 'DD-MM-YYYY': return `${day}-${month}-${year}`;
      case 'MM-DD-YYYY': return `${month}-${day}-${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
      default: return `${day}/${month}/${year}`;
    }
  }

  private async loadLanguage(language: SupportedLanguage): Promise<void> {
    try {
      const response = await fetch(`/assets/i18n/${language}.json?v=${TRANSLATION_VERSION}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Unable to load ${language} translations.`);
      this.translations.set(await response.json() as TranslationDictionary);
    } catch {
      if (language !== 'en') await this.loadLanguage('en');
    }
  }

  private loadInitialLanguage(): SupportedLanguage {
    const savedLanguage = localStorage.getItem('phd-compass-language');
    if (this.isSupportedLanguage(savedLanguage)) return savedLanguage;

    const browserLanguages = typeof navigator === 'undefined' ? [] : [navigator.language, ...Array.from(navigator.languages ?? [])];
    const browserLanguage = browserLanguages
      .map(language => language?.split('-')[0]?.toLowerCase())
      .find(language => this.isSupportedLanguage(language));

    if (this.isSupportedLanguage(browserLanguage)) return browserLanguage;
    return 'en';
  }

  private loadInitialDateFormat(): SupportedDateFormat {
    const savedFormat = localStorage.getItem(DATE_FORMAT_STORAGE_KEY);
    return this.isSupportedDateFormat(savedFormat) ? savedFormat : DEFAULT_DATE_FORMAT;
  }

  private isSupportedLanguage(language: string | null | undefined): language is SupportedLanguage {
    return language === 'en' || language === 'fr' || language === 'de';
  }

  private isSupportedDateFormat(format: string | null): format is SupportedDateFormat {
    return this.supportedDateFormats.includes(format as SupportedDateFormat);
  }
}
